import { useState, useCallback, useMemo } from "react";

import RefineEmailContentModal from "components/modals/refine-email-content-modal/refine-email-content-modal";
import type { RefineEmailContentFormValues } from "components/modals/refine-email-content-modal/refine-email-content-modal.types";
import { useRewriteCreativesWithWriter } from "components/modals/refine-creatives-content-modal/use-rewrite-creatives-with-writer";

import { useCurrentCampaign } from "hooks/api/use-campaigns";
import { useCurrentClient } from "hooks/api/use-clients";
import type { TouchpointAttributes } from "hooks/api/use-touchpoints";

import { getRewriteStrategyLinks } from "containers/admin/clients/campaign/rewrite-strategy-links";

/**
 * Rewrites one email from the creatives table. Goes through the campaign rewrite endpoint rather
 * than the preview page's version-scoped one: it resolves the latest draft server-side and updates
 * the list cache and polling session, which the table needs to show "Writing..." straight away.
 */
export const useRewriteEmailModal = () => {
  const currentClient = useCurrentClient();
  const currentCampaign = useCurrentCampaign();
  const [touchpointToRewrite, setTouchpointToRewrite] = useState<TouchpointAttributes | null>(null);

  const openRewriteEmailModal = useCallback((touchpoint: TouchpointAttributes) => {
    setTouchpointToRewrite(touchpoint);
  }, []);

  const closeRewriteEmailModal = useCallback(() => setTouchpointToRewrite(null), []);

  const touchpointIds = useMemo(
    () => (touchpointToRewrite ? [touchpointToRewrite.id] : undefined),
    [touchpointToRewrite],
  );

  const { rewriteCreatives, isSubmitting } = useRewriteCreativesWithWriter({
    clientId: currentClient.id,
    campaignId: currentCampaign.id,
    touchpointIds,
    onSuccess: closeRewriteEmailModal,
  });

  const handleRewrite = useCallback(
    (values: RefineEmailContentFormValues) =>
      rewriteCreatives({ creativesAdditionalInstructions: values.emailAdditionalInstructions }),
    [rewriteCreatives],
  );

  const rewriteEmailModalMarkup = useMemo(() => {
    if (!touchpointToRewrite) return null;

    return (
      <RefineEmailContentModal
        onCloseCallback={closeRewriteEmailModal}
        onConfirmCallback={handleRewrite}
        isSubmitting={isSubmitting}
        {...getRewriteStrategyLinks(currentClient.id, currentCampaign)}
      />
    );
  }, [
    touchpointToRewrite,
    currentClient.id,
    currentCampaign,
    isSubmitting,
    closeRewriteEmailModal,
    handleRewrite,
  ]);

  return {
    openRewriteEmailModal,
    rewriteEmailModalMarkup,
  };
};
